import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import axios from "axios";
import { userApi } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useUserAuth } from "../context/UserAuthContext";
import { Heart, Plus, Star, ArrowLeft, Package } from "lucide-react";
import { Navbar } from "./Navbar";
import { Footer } from "./Footer";
import { extractProductId, productPath } from "../lib/productSlug";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StarsInput = ({ value, onChange }) => (
  <div className="flex gap-1">
    {[1, 2, 3, 4, 5].map((n) => (
      <button key={n} type="button" data-testid={`star-${n}`} onClick={() => onChange(n)}
        className={`p-1 ${n <= value ? "text-yellow-400" : "text-zinc-700"} hover:text-yellow-400 transition`}>
        <Star className="w-7 h-7" strokeWidth={2.5} fill={n <= value ? "currentColor" : "none"} />
      </button>
    ))}
  </div>
);

const StarsDisplay = ({ value, size = 4 }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((n) => (
      <Star key={n} className={`w-${size} h-${size} ${n <= value ? "text-yellow-400" : "text-zinc-700"}`}
        strokeWidth={2.5} fill={n <= value ? "currentColor" : "none"} />
    ))}
  </div>
);

export default function ProductDetailPage() {
  const { id: slugOrId } = useParams();
  const id = extractProductId(slugOrId);
  const { t, i18n } = useTranslation();
  const { addItem } = useCart();
  const { user } = useUserAuth();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [wishlistIds, setWishlistIds] = useState([]);
  const [loading, setLoading] = useState(true);

  // Review form state
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [myReviewId, setMyReviewId] = useState(null);

  const loadProduct = async () => {
    try {
      const [p, r] = await Promise.all([
        axios.get(`${API}/products/${id}`),
        axios.get(`${API}/products/${id}/reviews`),
      ]);
      setProduct(p.data);
      setReviews(r.data);
    } catch (e) {
      toast.error("Prodotto non trovato");
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  const loadMyReview = async () => {
    if (!user) { setMyReviewId(null); return; }
    try {
      const { data } = await userApi.get(`/products/${id}/reviews/me`);
      if (data) {
        setRating(data.rating);
        setComment(data.comment);
        setMyReviewId(data.id);
      } else {
        setMyReviewId(null);
      }
    } catch {}
  };

  const loadWishlist = async () => {
    if (!user) return;
    try {
      const { data } = await userApi.get("/users/me/wishlist");
      setWishlistIds(data.map((p) => p.id));
    } catch {}
  };

  useEffect(() => { loadProduct(); }, [id]);
  useEffect(() => { loadWishlist(); loadMyReview(); }, [user, id]);

  // Auto-redirect legacy /product/p123 to the SEO-friendly slug URL once the product loads
  useEffect(() => {
    if (product && slugOrId && slugOrId === product.id) {
      const canonical = productPath(product);
      if (canonical && canonical !== `/product/${slugOrId}`) {
        navigate(canonical, { replace: true });
      }
    }
  }, [product]);

  if (loading) return <div className="min-h-screen bg-zinc-950 flex items-center justify-center text-zinc-500 uppercase">...</div>;
  if (!product) return null;

  const isWishlisted = wishlistIds.includes(product.id);
  const avgRating = reviews.length ? reviews.reduce((s, r) => s + r.rating, 0) / reviews.length : 0;

  const toggleWishlist = async () => {
    if (!user) {
      toast.error(t("product.login_to_wishlist"));
      navigate("/login", { state: { from: `/product/${id}` } });
      return;
    }
    try {
      if (isWishlisted) {
        await userApi.delete(`/users/me/wishlist/${product.id}`);
        setWishlistIds((ids) => ids.filter((x) => x !== product.id));
        toast.success(t("product.wishlist_removed"));
      } else {
        await userApi.post(`/users/me/wishlist/${product.id}`);
        setWishlistIds((ids) => [...ids, product.id]);
        toast.success(t("product.wishlist_added"));
      }
    } catch (e) {
      toast.error("Error");
    }
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (!user) {
      navigate("/login", { state: { from: `/product/${id}` } });
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await userApi.post(`/products/${product.id}/reviews`, { rating, comment });
      setMyReviewId(data.id);
      toast.success(t("product.review_submitted"));
      const r = await axios.get(`${API}/products/${product.id}/reviews`);
      setReviews(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-zinc-950 min-h-screen text-white">
      <Navbar />
      <main className="pt-28 pb-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/shop" data-testid="product-back-to-shop" className="text-zinc-500 hover:text-yellow-400 text-xs uppercase tracking-widest flex items-center gap-2 mb-6">
          <ArrowLeft className="w-4 h-4" /> {t("product.back_to_shop")}
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          {/* Image */}
          <div className="relative">
            <div className="border-4 border-yellow-400 brutal-shadow bg-zinc-900 aspect-square overflow-hidden">
              <img src={product.image} alt={product.title} className="w-full h-full object-contain p-4" data-testid="product-detail-image" />
              <div className="absolute top-4 left-4 flex gap-2">
                {product.bestseller && (
                  <span className="bg-yellow-400 text-black px-3 py-1 font-heading text-xs uppercase tracking-widest">{t("product.bestseller")}</span>
                )}
                {product.limited && (
                  <span className="bg-black border-2 border-yellow-400 text-yellow-400 px-3 py-1 font-heading text-xs uppercase tracking-widest">{t("product.limited")}</span>
                )}
              </div>
            </div>
          </div>

          {/* Details */}
          <div className="space-y-6">
            <div>
              <div className="font-jp text-yellow-400 text-xs tracking-widest mb-2">{product.category.toUpperCase()} · #{product.id}</div>
              <h1 data-testid="product-detail-title" className="font-heading text-5xl uppercase text-white leading-none">{product.title}</h1>
              {reviews.length > 0 && (
                <div className="flex items-center gap-3 mt-3">
                  <StarsDisplay value={Math.round(avgRating)} size={5} />
                  <span className="text-zinc-400 text-sm">
                    {avgRating.toFixed(1)} · {t("product.rating_count", { count: reviews.length })}
                  </span>
                </div>
              )}
            </div>

            <div className="font-heading text-5xl text-yellow-400" data-testid="product-detail-price">€ {product.price.toFixed(2)}</div>

            <p className="text-zinc-300 text-lg leading-relaxed border-l-4 border-yellow-400 pl-5">{product.description}</p>

            <div className="text-sm uppercase tracking-widest">
              {product.stock > 0 ? (
                <span className="text-green-400">{t("product.in_stock", { n: product.stock })}</span>
              ) : (
                <span className="text-red-400">{t("product.out_of_stock")}</span>
              )}
            </div>

            <div className="flex gap-3">
              <button data-testid="detail-add-to-cart" onClick={() => addItem(product)} disabled={product.stock === 0}
                className="flex-1 bg-yellow-400 text-black font-heading uppercase text-lg py-4 hover:bg-white transition flex items-center justify-center gap-2 disabled:opacity-40">
                <Plus className="w-5 h-5" strokeWidth={3} /> {t("product.add_to_cart")}
              </button>
              <button data-testid="detail-wishlist" onClick={toggleWishlist}
                className={`border-4 ${isWishlisted ? "bg-yellow-400 border-yellow-400 text-black" : "border-zinc-700 text-zinc-300"} px-5 hover:border-yellow-400 hover:text-yellow-400 transition`}>
                <Heart className="w-6 h-6" strokeWidth={3} fill={isWishlisted ? "currentColor" : "none"} />
              </button>
            </div>
          </div>
        </div>

        {/* Reviews */}
        <section className="mt-20 border-t-2 border-zinc-800 pt-10">
          <h2 className="font-heading text-4xl uppercase text-white mb-8 flex items-center gap-3">
            <Star className="w-7 h-7 text-yellow-400" strokeWidth={3} /> {t("product.reviews")} ({reviews.length})
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Review form */}
            <div className="lg:col-span-1">
              {user ? (
                <form onSubmit={submitReview} data-testid="review-form" className="bg-zinc-900 border-2 border-zinc-800 p-5 space-y-4 sticky top-24">
                  <h3 className="font-heading uppercase text-yellow-400">
                    {myReviewId ? t("product.edit_review") : t("product.write_review")}
                  </h3>
                  <div>
                    <label className="font-heading uppercase text-zinc-300 text-xs tracking-widest mb-2 block">{t("product.your_rating")}</label>
                    <StarsInput value={rating} onChange={setRating} />
                  </div>
                  <div>
                    <label className="font-heading uppercase text-zinc-300 text-xs tracking-widest mb-2 block">{t("product.your_comment")}</label>
                    <textarea data-testid="review-comment" required rows={4} value={comment} onChange={(e) => setComment(e.target.value)}
                      className="w-full bg-zinc-950 border-2 border-zinc-800 px-3 py-2 text-white focus:border-yellow-400 focus:outline-none" />
                  </div>
                  <button data-testid="submit-review" disabled={submitting} type="submit"
                    className="w-full bg-yellow-400 text-black font-heading uppercase py-3 hover:bg-white transition disabled:opacity-40">
                    {submitting ? "..." : (myReviewId ? t("product.update_review") : t("product.submit_review"))}
                  </button>
                </form>
              ) : (
                <div className="bg-zinc-900 border-2 border-zinc-800 p-5 text-center">
                  <Package className="w-8 h-8 text-yellow-400 mx-auto mb-3" strokeWidth={3} />
                  <p className="text-zinc-400 text-sm mb-3">{t("product.login_to_review")}</p>
                  <Link to="/login" data-testid="login-to-review" state={{ from: `/product/${id}` }}
                    className="inline-block bg-yellow-400 text-black font-heading uppercase px-5 py-2 hover:bg-white transition">
                    {t("auth.login")}
                  </Link>
                </div>
              )}
            </div>

            {/* Reviews list */}
            <div className="lg:col-span-2 space-y-4">
              {reviews.length === 0 && (
                <div data-testid="no-reviews" className="bg-zinc-900 border-2 border-zinc-800 p-8 text-center text-zinc-500 uppercase tracking-widest">
                  {t("product.no_reviews")}
                </div>
              )}
              {reviews.map((r) => (
                <div key={r.id} data-testid={`review-${r.id}`} className="bg-zinc-900 border-2 border-zinc-800 p-5">
                  <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                    <div>
                      <div className="text-white font-bold">{r.user_name}</div>
                      <div className="text-zinc-500 text-xs">{new Date(r.created_at).toLocaleDateString(i18n.language)}</div>
                    </div>
                    <StarsDisplay value={r.rating} size={4} />
                  </div>
                  <p className="text-zinc-300 leading-relaxed">{r.comment}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
